import { Container, Stack, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ErrorPanel, globalDateFormat } from "@qubit/autoparts";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetPaginatedOrderSummariesInfiniteQuery } from "~/redux/warehouse/orders.hooks";

import {
  FilterRow,
  FILTER_ALL_ORDERS,
  FILTER_DUE_SOON,
  FILTER_PAST_DUE
} from "./FilterRow";

import { OrdersTable } from "./OrdersTable";

import { setSelectedDate } from "./orders.slice";

type Props = { viewTitle?: ViewNameTranslation };

export function Orders(props: Props) {
  const { viewTitle } = props;
  useNavbar({
    viewTitle
  });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] =
    useState<string>(FILTER_ALL_ORDERS);

  const { ord_includeSpokes } = useAppSelector(selectServerConfig);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const orderFilter = useAppSelector((state) => state.ordersSlice.orderFilter);
  const orderStatusFilters = useAppSelector(
    (state) => state.ordersSlice.orderStatusFilters
  );
  const orderTypeFilters = useAppSelector(
    (state) => state.ordersSlice.orderTypeFilters
  );
  const selectedDate = useAppSelector(
    (state) => state.ordersSlice.selectedDate
  );
  const selectedOrderIds = useAppSelector(
    (state) => state.ordersSlice.selectedOrderIds
  );

  const pickingEndTimeParams = useMemo(() => {
    if (selectedFilter === FILTER_DUE_SOON) {
      const now = dayjs();
      const dueSoonEndTime = now.add(2, "hour");
      return {
        petStart: now.toISOString(),
        petEnd: dueSoonEndTime.toISOString()
      };
    }
    if (selectedFilter === FILTER_PAST_DUE) {
      return { petEnd: dayjs().toISOString() };
    }
    return {};
  }, [selectedFilter]);

  const {
    data: paginatedOrders,
    error: ordersError,
    isLoading: isOrdersLoading,
    fetchNextPage
  } = useGetPaginatedOrderSummariesInfiniteQuery({
    date: selectedDate,
    fcTimeZone: fulfillmentCenter?.timeZone,
    includeSpokes: ord_includeSpokes,
    search: orderFilter,
    statuses: orderStatusFilters,
    orderTypes: orderTypeFilters,
    ...pickingEndTimeParams
  });
  const orders = paginatedOrders?.pages.flat();

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      dispatch(setSelectedDate(date || null));
    },
    [dispatch]
  );

  const datePicker = (
    <DesktopDatePicker
      slotProps={{
        textField: {
          InputLabelProps: {
            shrink: true
          },
          sx: {
            ".MuiInputBase-input": {
              fontSize: "1rem"
            },
            width: "320px"
          },
          label: t("date"),
          variant: "outlined"
        }
      }}
      label={t("date")}
      value={selectedDate ? dayjs(selectedDate) : null}
      format={globalDateFormat}
      onChange={(date) => handleDateChange(date?.toDate() || null)}
    />
  );

  useEffect(() => {
    if (!selectedDate) {
      dispatch(setSelectedDate(new Date()));
    }
  }, [dispatch, selectedDate]);

  return (
    <Container sx={{ p: 4 }}>
      <Stack direction="column" gap={2} mb={2}>
        <Typography variant="h4">{t("orders")}</Typography>
        {datePicker}
        <FilterRow
          currentSelectedFilter={selectedFilter}
          handleFilterChange={setSelectedFilter}
        />
        {!ordersError && (
          <OrdersTable
            fulfillmentCenter={fulfillmentCenter}
            orders={orders || []}
            ordersLoading={isOrdersLoading}
            ordersError={ordersError}
            selectedDate={selectedDate}
            selectedOrderIds={selectedOrderIds}
            title={t("orders")}
            fetchNextPage={fetchNextPage}
          />
        )}
      </Stack>
      {!!ordersError && (
        <ErrorPanel message={getMessageFromRtkError(ordersError)} />
      )}
    </Container>
  );
}
